// src/pages/PeopleInsightsDashboard.tsx — People Insights (PCD/HR) Analytics Dashboard
//
// 8-tab dashboard: Demographics | New Starters | Departures | Leave Balances |
//                  Excessive Leave | Leave Taken | Health & Safety | EAP / LTIFR
// Data source: /api/people-insights/* (PayGlobal via dw_gold Fabric Warehouse)
// Requires governed access to dashboards:people_insights.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, Dropdown, IDropdownOption,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
  TooltipHost, DirectionalHint,
} from '@fluentui/react';
import {
  BarChart, Bar, AreaChart, Area, LabelList, PieChart, Pie, Legend,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  CaretLeft, ArrowsClockwise,
  Users, Info, ShieldCheck, Heartbeat,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const CYAN  = '#06B6D4';   // People Insights accent
const GREEN = '#2e873d';
const AMBER = '#D97706';
const RED   = '#D32F2F';

const CYAN_PALETTE = [
  '#06B6D4','#0891B2','#22D3EE','#67E8F9','#0E7490',
  '#155E75','#38BDF8','#7DD3FC','#0369A1','#075985',
];

// Fixed colours for the gender series so every gender-split chart reads the same.
const GENDER_COLOURS: Record<string, string> = {
  'Female':            '#06B6D4',
  'Male':              '#0369A1',
  'Another gender':    '#7C3AED',
  'Prefer not to say': '#9CA3AF',
};

const DIRECTORATE_COLOURS: Record<string, string> = {
  'Places and Spaces':           '#0891B2',
  'Urban Living':                '#06B6D4',
  'Customer and Transformation': '#7C3AED',
  'Community Services':          '#2e873d',
  'Office of the CEO':           '#D97706',
  'Unassigned':                  '#9CA3AF',
};
const DIR_PALETTE = ['#06B6D4','#0891B2','#7C3AED','#2e873d','#D97706','#DC2626','#0369A1','#6B7280'];
function dirColour(name: string, idx: number): string {
  return DIRECTORATE_COLOURS[name] ?? DIR_PALETTE[idx % DIR_PALETTE.length];
}

// Leave-type colours
const LEAVE_TYPE_COLOURS: Record<string, string> = {
  AL:  '#06B6D4',
  SL:  '#D97706',
  LSL: '#7C3AED',
  RDO: '#2e873d',
};
function leaveColour(code: string, idx: number): string {
  return LEAVE_TYPE_COLOURS[code] ?? CYAN_PALETTE[idx % CYAN_PALETTE.length];
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
type TabKey =
  | 'demographics' | 'newStarters' | 'departures' | 'departureTrends'
  | 'leaveBalances' | 'excessiveLeave' | 'leaveTaken'
  | 'healthSafety' | 'eapLtifr';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'demographics',    label: 'Demographics'      },
  { key: 'newStarters',     label: 'New Starters'      },
  { key: 'departures',      label: 'Departures'        },
  { key: 'departureTrends', label: 'Departure Trends'  },
  { key: 'leaveBalances',   label: 'Leave Balances'    },
  { key: 'excessiveLeave',  label: 'Excessive Leave'   },
  { key: 'leaveTaken',      label: 'Leave Taken'       },
  { key: 'healthSafety',    label: 'Health & Safety'   },
  { key: 'eapLtifr',        label: 'EAP / LTIFR'       },
];

// Tabs that expose the leave-type filter
const LEAVE_TABS: TabKey[] = ['leaveBalances', 'excessiveLeave', 'leaveTaken'];
// Date-driven tabs get the Year + Month slicers (matching the report).
const DATE_TABS: TabKey[] = ['newStarters', 'departures', 'departureTrends', 'leaveTaken'];
const MONTHS: { key: number; text: string }[] = [
  { key: 1, text: 'Jan' }, { key: 2, text: 'Feb' }, { key: 3, text: 'Mar' },
  { key: 4, text: 'Apr' }, { key: 5, text: 'May' }, { key: 6, text: 'Jun' },
  { key: 7, text: 'Jul' }, { key: 8, text: 'Aug' }, { key: 9, text: 'Sep' },
  { key: 10, text: 'Oct' }, { key: 11, text: 'Nov' }, { key: 12, text: 'Dec' },
];
// Tabs that are "coming soon" placeholders (no fetch)
const PLACEHOLDER_TABS: TabKey[] = ['healthSafety', 'eapLtifr'];

// ── Types ─────────────────────────────────────────────────────────────────────
interface FilterOptions {
  directorates:       string[];
  allDepartments:     string[];
  dirHierarchy:       Record<string, string[]>;
  employmentStatuses: string[];
  leaveTypes:         string[];
  genders:            string[];
  years:              number[];
}

interface GenderSplitRow { label: string; [gender: string]: number | string }
interface DemographicsData {
  kpis: {
    headcount: number; totalFTE: number | null;
    fullTimePct: number; partTimePct: number; casualPct: number;
    avgTenureYears: number | null;
    avgAgeYears: number | null;
  };
  byDirectorate:    { directorate: string; count: number }[];
  genderSeries:     string[];
  byStatusGender:   GenderSplitRow[];
  byAgeGender:      GenderSplitRow[];
  byTenureGender:   GenderSplitRow[];
  byGender:         { gender: string; count: number }[];
  meta: { generatedAt: string };
}

// New-starters breakdown row — aggregated by department (not individual staff).
interface StarterDeptRow {
  directorate: string; department: string;
  starters: number; fullTime: number; partTime: number; casual: number;
}
interface NewStartersData {
  kpis: { totalStarters: number; fullTime: number; partTime: number; casual: number; avgAgeYears: number | null };
  genderSeries:     string[];
  byStatusGender:   GenderSplitRow[];
  byAgeGender:      GenderSplitRow[];
  byGender:         { gender: string; count: number }[];
  byMonth:          { label: string; year: number; month: number; count: number }[];
  byDirectorate:    { directorate: string; count: number }[];
  detail: StarterDeptRow[];
  meta: { dateFrom: number; dateTo: number; asAtDate: string | null; generatedAt: string };
}

// Departure-trends breakdown row — reason with share of total (not staff names).
interface ReasonRow { reason: string; count: number; pct: number }
interface DeparturesData {
  kpis: { totalLeavers: number; activeHeadcount: number; turnoverPct: number | null };
  byMonth:       { label: string; year: number; month: number; count: number }[];
  byDirectorate: { directorate: string; count: number }[];
  byReason:      { reason: string; count: number }[];
  genderSeries:  string[];
  byGender:      { gender: string; count: number }[];
  byStatus:      { status: string; count: number }[];
  byStatusGender: GenderSplitRow[];
  byAgeGender:    GenderSplitRow[];
  byTenureGender: GenderSplitRow[];
  detail: ReasonRow[];
  meta: { dateFrom: number; dateTo: number; asAtDate: string | null; generatedAt: string };
}

// Leave-balance breakdown row — average balance per employee by department.
interface BalanceDeptRow {
  directorate: string; department: string; employees: number;
  AL: number | null; SL: number | null; LSL: number | null; RDO: number | null;
}
interface LeaveBalancesData {
  byLeaveType: {
    leaveType: string; totalUnits: number; avgUnits: number; employeeCount: number;
  }[];
  byDirectorate: (Record<string, number> & { directorate: string })[];
  detail: BalanceDeptRow[];
  meta: { generatedAt: string };
}

// Excessive-leave breakdown row — Annual Leave band summary by directorate.
interface ExcessiveDirRow {
  directorate: string; over40: number; band3040: number; employees: number;
  avgAL: number | null; maxAL: number | null;
}
interface DeptBandRow { department: string; total: number; [band: string]: number | string }
interface ExcessiveLeaveData {
  thresholdDays: number;
  bands: string[];
  bandTotals: Record<string, number>;
  byDepartment: DeptBandRow[];
  kpis: { overThreshold: number; band3040: number; under30: number };
  detail: ExcessiveDirRow[];
  meta: { generatedAt: string };
}

// Leave-taken breakdown row — by leave type (not individual staff records).
interface LeaveTypeRow {
  allowanceType: string; group: string; hours: number; employees: number; avgHours: number;
}
interface LeaveMonthRow { key: string; label: string; year: number; month: number; [group: string]: number | string }
interface LeaveTakenData {
  kpis: { totalHours: number; employees: number };
  leaveGroups: string[];
  byGroup: { group: string; hours: number }[];
  byLeaveType: { leaveType: string; group: string; hours: number }[];
  byMonthGroup: LeaveMonthRow[];
  detail: LeaveTypeRow[];
  meta: { dateFrom: number; dateTo: number; asAtDate: string | null; generatedAt: string };
}

// ── Date range helpers ────────────────────────────────────────────────────────
const DATE_RANGE_OPTIONS: IDropdownOption[] = [
  { key: '30d',  text: 'Last 30 days'   },
  { key: '90d',  text: 'Last 3 months'  },
  { key: '180d', text: 'Last 6 months'  },
  { key: '365d', text: 'Last 12 months' },
  { key: '730d', text: 'Last 24 months' },
  { key: 'all',  text: 'All time'       },
];
// The date-driven tabs (New Starters / Departures / Leave Taken) anchor their
// default window server-side to the LATEST data date, not "today" — so the
// period preset is sent by name and the backend resolves it against the anchor.
// This keeps the dashboard working against stale dev data and live prod data.

// ── Leave unit toggle (Hours ↔ Days) ──────────────────────────────────────────
// Leave balances and leave-taken are stored in HOURS. A standard working day is
// 7.6 hours (38-hour week) — confirmed. HR reads days more intuitively, so the
// toggle defaults to Days and divides every displayed leave value by 7.6.
const HOURS_PER_DAY = 7.6;
type LeaveUnit = 'hours' | 'days';
// Convert an hours value to the selected display unit, rounded to 1 dp.
function toUnit(hours: number | null | undefined, unit: LeaveUnit): number | null {
  if (hours == null) return null;
  const v = unit === 'days' ? hours / HOURS_PER_DAY : hours;
  return Math.round(v * 10) / 10;
}

// ── Calculation tooltip ────────────────────────────────────────────────────────
// A small hover-only info icon that documents how a figure is derived. Icon only
// — no visible body text — placed beside the relevant label.
function InfoTip({ text }: { text: string }) {
  return (
    <TooltipHost
      content={text}
      directionalHint={DirectionalHint.topCenter}
      styles={{ root: { display: 'inline-flex', marginLeft: 5, verticalAlign: 'middle' } }}
    >
      <Info size={13} color="#9CA3AF" weight="bold" style={{ cursor: 'help' }} aria-label="How this is calculated" />
    </TooltipHost>
  );
}

function fmtNum(n: number): string {
  return (n ?? 0).toLocaleString('en-AU');
}
function fmtDec(n: number | null | undefined, dp = 1): string {
  return n != null ? Number(n).toFixed(dp) : '—';
}
function trunc(s: string, max = 26): string {
  return s && s.length > max ? s.slice(0, max) + '…' : (s || '');
}

// ── Spin animation ────────────────────────────────────────────────────────────
const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles ────────────────────────────────────────────────────────────────────
const s = mergeStyleSets({
  page: {
    padding: 32, minHeight: 'calc(100vh - 52px)',
    background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif",
    '@media (max-width:768px)': { padding: 16 },
  } as any,
  container: { maxWidth: 1280, margin: '0 auto' },

  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexWrap: 'wrap' as const, gap: 8,
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    color: CYAN, fontWeight: 600, fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0, marginBottom: 4,
  },
  pageTitle: {
    fontSize: 26, fontWeight: 800, color: '#1A1A1A',
    letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10,
  },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', background: '#fff', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#484848',
    cursor: 'pointer', transition: 'all 0.15s',
    selectors: { ':hover': { borderColor: CYAN, color: CYAN } },
  } as any,

  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  filterLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  clearBtn: {
    padding: '7px 14px', background: 'none', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#747474',
    cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { borderColor: RED, color: RED } },
  } as any,

  // Tab bar
  tabBar: {
    display: 'flex', borderBottom: '2px solid #E1E1E1',
    marginTop: 16, marginBottom: 24, gap: 0, flexWrap: 'wrap' as const,
  },
  tabBtn: {
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
    borderBottom: '2px solid transparent', marginBottom: -2,
  },

  // KPI rows
  kpiRow3: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24,
    '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
  } as any,
  kpiRow4: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24,
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
  } as any,
  kpiCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  kpiLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em', marginBottom: 6,
  },
  kpiValue: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: '1', fontVariantNumeric: 'tabular-nums' },
  kpiSub: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  kpiBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginTop: 6,
  },

  twoCol: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  } as any,
  sectionCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 2 },
  sectionSub:   { fontSize: 12, color: '#9CA3AF', marginBottom: 14 },

  centeredMsg: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', minHeight: 240, gap: 12,
    color: '#747474', fontSize: 14, textAlign: 'center' as const,
  },
  accessBox: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: 56, textAlign: 'center' as const, color: '#747474',
  },
  accessTitle: { fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 },
});

const tooltipStyle = {
  backgroundColor: '#fff', border: '1px solid #E1E1E1',
  borderRadius: 6, color: '#232323', fontSize: 12,
};

// ── Tooltip components ────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || CYAN }}>
          {p.name}: {fmtNum(p.value)}
        </div>
      ))}
    </div>
  );
}
function CountTooltip({ active, payload, label, total, noun }: any) {
  if (!active || !payload?.length) return null;
  const count = payload[0]?.value || 0;
  const pct   = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div>{fmtNum(count)} {noun || 'employees'} ({pct}%)</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PeopleInsightsDashboard() {
  const nav = useNavigate();

  // Access gate
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>('demographics');

  // Shared filter state — multi-select arrays (empty = All)
  const [dateRange,        setDateRange]        = useState('365d');
  const [directorate,      setDirectorate]      = useState<string[]>([]);
  const [department,       setDepartment]       = useState<string[]>([]);
  const [employmentStatus, setEmploymentStatus] = useState<string[]>([]);
  const [leaveType,        setLeaveType]        = useState<string[]>([]);
  const [gender,           setGender]           = useState<string[]>([]);
  const [year,             setYear]             = useState<number[]>([]);
  const [month,            setMonth]            = useState<number[]>([]);
  const [leaveGroup,       setLeaveGroup]       = useState<string[]>([]);
  const [excessiveCategory, setExcessiveCategory] = useState<string[]>([]);
  const [tenureGroup,      setTenureGroup]      = useState<string[]>([]);

  // Leave-value display unit (Hours ↔ Days). Display-only transform — the
  // backend always returns hours; Days divides displayed numbers by 7.6.
  // Defaults to Days (more intuitive for HR).
  const [leaveUnit,        setLeaveUnit]        = useState<LeaveUnit>('days');

  // Filter options (loaded once on mount)
  const [filterOpts,        setFilterOpts]        = useState<FilterOptions | null>(null);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);

  // Per-tab data & state
  const [demographicsData,   setDemographicsData]   = useState<DemographicsData | null>(null);
  const [newStartersData,    setNewStartersData]    = useState<NewStartersData | null>(null);
  const [departuresData,     setDeparturesData]     = useState<DeparturesData | null>(null);
  const [leaveBalancesData,  setLeaveBalancesData]  = useState<LeaveBalancesData | null>(null);
  const [excessiveLeaveData, setExcessiveLeaveData] = useState<ExcessiveLeaveData | null>(null);
  const [leaveTakenData,     setLeaveTakenData]     = useState<LeaveTakenData | null>(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // ── Access check ────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        if (d.role === 'admin') {
          setHasAccess(true);
        } else {
          setHasAccess(Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('people_insights'));
        }
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  // ── Load filter options (once after access confirmed) ────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    setFilterOptsLoading(true);
    accessService.get<FilterOptions>('/people-insights/filter-options')
      .then(r => setFilterOpts(r.data))
      .catch(e => console.warn('[PeopleInsights] filter-options failed:', e.message))
      .finally(() => setFilterOptsLoading(false));
  }, [hasAccess]);

  // ── Common filter params ─────────────────────────────────────────────────────
  const filterParams = useCallback(() => {
    // Send the period by NAME (preset). The date-driven endpoints resolve it
    // against a data-latest anchor date server-side, so no dateFrom/dateTo is
    // computed relative to "today" on the client.
    return {
      preset: dateRange,
      ...(directorate.length      ? { directorate:      directorate.join(',') }      : {}),
      ...(department.length       ? { department:       department.join(',') }       : {}),
      ...(employmentStatus.length ? { employmentStatus: employmentStatus.join(',') } : {}),
      ...(leaveType.length        ? { leaveType:        leaveType.join(',') }        : {}),
      ...(gender.length           ? { gender:           gender.join(',') }           : {}),
      ...(year.length             ? { year:             year.join(',') }             : {}),
      ...(month.length            ? { month:            month.join(',') }            : {}),
      ...(leaveGroup.length       ? { leaveGroup:       leaveGroup.join(',') }       : {}),
      ...(excessiveCategory.length ? { excessiveCategory: excessiveCategory.join(',') } : {}),
      ...(tenureGroup.length      ? { tenureGroup:      tenureGroup.join(',') }      : {}),
    };
  }, [dateRange, directorate, department, employmentStatus, leaveType, gender, year, month, leaveGroup, excessiveCategory, tenureGroup]);

  // ── Clear all tab data when filters change ───────────────────────────────────
  useEffect(() => {
    setDemographicsData(null);
    setNewStartersData(null);
    setDeparturesData(null);
    setLeaveBalancesData(null);
    setExcessiveLeaveData(null);
    setLeaveTakenData(null);
  }, [dateRange, directorate, department, employmentStatus, leaveType, gender, year, month, leaveGroup, excessiveCategory, tenureGroup]);

  // ── Fetch helper (no dirty-gate — always loads when called) ─────────────────
  const fetchCurrentTab = useCallback(async (tab: TabKey) => {
    if (!hasAccess) return;
    if (PLACEHOLDER_TABS.includes(tab)) return; // coming-soon tabs never fetch
    setLoading(true);
    setError(null);
    try {
      const params = filterParams();
      if (tab === 'demographics') {
        const r = await accessService.get<DemographicsData>('/people-insights/demographics', { params });
        setDemographicsData(r.data);
      } else if (tab === 'newStarters') {
        const r = await accessService.get<NewStartersData>('/people-insights/new-starters', { params });
        setNewStartersData(r.data);
      } else if (tab === 'departures' || tab === 'departureTrends') {
        const r = await accessService.get<DeparturesData>('/people-insights/departures', { params });
        setDeparturesData(r.data);
      } else if (tab === 'leaveBalances') {
        const r = await accessService.get<LeaveBalancesData>('/people-insights/leave-balances', { params });
        setLeaveBalancesData(r.data);
      } else if (tab === 'excessiveLeave') {
        const r = await accessService.get<ExcessiveLeaveData>('/people-insights/excessive-leave', { params });
        setExcessiveLeaveData(r.data);
      } else if (tab === 'leaveTaken') {
        const r = await accessService.get<LeaveTakenData>('/people-insights/leave-taken', { params });
        setLeaveTakenData(r.data);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, filterParams]);

  // Fire whenever: tab switches OR filters change (filterParams ref changes → fetchCurrentTab ref changes)
  useEffect(() => {
    if (!hasAccess) return;
    fetchCurrentTab(activeTab);
  }, [activeTab, fetchCurrentTab, hasAccess]);

  // ── Cascade reset (directorate → department) ────────────────────────────────
  useEffect(() => {
    if (!directorate.length || !filterOpts) return;
    const allowed = new Set(directorate.flatMap(d => filterOpts.dirHierarchy[d] ?? []));
    setDepartment(prev => {
      const next = prev.filter(dep => allowed.has(dep));
      return next.length === prev.length ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorate, filterOpts]);

  const clearFilters = () => {
    setDateRange('365d');
    setDirectorate([]); setDepartment([]);
    setEmploymentStatus([]); setLeaveType([]); setGender([]);
    setYear([]); setMonth([]); setLeaveGroup([]);
    setExcessiveCategory([]); setTenureGroup([]);
  };
  const hasActiveFilter =
    directorate.length > 0 || department.length > 0 ||
    employmentStatus.length > 0 || leaveType.length > 0 || dateRange !== '365d';

  // ── Derived filter option lists (cascade, multi-select aware) ───────────────
  const availableDepts: string[] = React.useMemo(() => {
    if (!directorate.length || !filterOpts?.dirHierarchy) return filterOpts?.allDepartments ?? [];
    return [...new Set(directorate.flatMap(d => filterOpts!.dirHierarchy[d] ?? []))].sort();
  }, [directorate, filterOpts]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const refreshedAt = (() => {
    const d = demographicsData?.meta.generatedAt || newStartersData?.meta.generatedAt
      || departuresData?.meta.generatedAt || leaveBalancesData?.meta.generatedAt
      || excessiveLeaveData?.meta.generatedAt || leaveTakenData?.meta.generatedAt;
    return d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : null;
  })();

  // "As at" data-anchor date for the active date-driven tab. The default period
  // window is anchored to this latest-data date (not "today"), so surface it.
  const asAtDate = (() => {
    if (activeTab === 'newStarters') return newStartersData?.meta.asAtDate ?? null;
    if (activeTab === 'departures' || activeTab === 'departureTrends') return departuresData?.meta.asAtDate ?? null;
    if (activeTab === 'leaveTaken')  return leaveTakenData?.meta.asAtDate ?? null;
    return null;
  })();

  const showLeaveFilter = LEAVE_TABS.includes(activeTab);
  const showDateFilter  = DATE_TABS.includes(activeTab);
  // Leave tabs also expose the Hours/Days unit toggle.
  const unitLabel = leaveUnit === 'days' ? 'days' : 'hours';
  const uv = React.useCallback(
    (hours: number | null | undefined) => toUnit(hours, leaveUnit),
    [leaveUnit]
  );

  // ── Access gate ───────────────────────────────────────────────────────────────
  if (hasAccess === null) {
    return (
      <div className={s.page}>
        <div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <Spinner size={SpinnerSize.large} label="Checking access…" />
        </div>
      </div>
    );
  }
  if (hasAccess === false) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <button className={s.backBtn} onClick={() => nav('/dashboards')}>
            <CaretLeft size={12} weight="bold" /> Dashboards
          </button>
          <div className={s.accessBox}>
            <div className={s.accessTitle}>Access Required</div>
            <div style={{ fontSize: 14, color: '#747474', maxWidth: 440, margin: '0 auto' }}>
              The People Insights dashboard requires governed access.
              Submit an access request to your administrator to get started.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={s.page}>
      <div className={s.container}>

        {/* Back nav */}
        <button className={s.backBtn} onClick={() => nav('/dashboards')}>
          <CaretLeft size={12} weight="bold" /> Dashboards
        </button>

        {/* Header */}
        <div className={s.headerRow}>
          <div>
            <div className={s.pageTitle}>
              <span style={{
                width: 36, height: 36, borderRadius: 8, background: CYAN,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Users size={18} color="#fff" weight="bold" />
              </span>
              People Insights
            </div>
            <div className={s.pageSub}>
              PayGlobal HR · {refreshedAt ? `Updated ${refreshedAt}` : 'Loading…'}
              {asAtDate && <> · data as at {asAtDate}</>}
            </div>
          </div>
          <button
            className={s.refreshBtn}
            onClick={() => {
              if (activeTab === 'demographics')   setDemographicsData(null);
              if (activeTab === 'newStarters')    setNewStartersData(null);
              if (activeTab === 'departures' || activeTab === 'departureTrends') setDeparturesData(null);
              if (activeTab === 'leaveBalances')  setLeaveBalancesData(null);
              if (activeTab === 'excessiveLeave') setExcessiveLeaveData(null);
              if (activeTab === 'leaveTaken')     setLeaveTakenData(null);
              fetchCurrentTab(activeTab);
            }}
            disabled={loading || PLACEHOLDER_TABS.includes(activeTab)}
          >
            <ArrowsClockwise size={14} weight="bold"
              style={loading ? { animation: `${spinAnim} 1s linear infinite` } : {}} />
            Refresh
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}
            styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>
            {error}
          </MessageBar>
        )}

        {/* Filter bar */}
        <div style={{
          background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8,
          padding: '14px 20px', marginBottom: 0,
          display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10,
        }}>

          {/* Period */}
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Period</span>
            <Dropdown options={DATE_RANGE_OPTIONS} selectedKey={dateRange}
              onChange={(_, o) => o && setDateRange(String(o.key))}
              styles={{ root: { minWidth: 148 }, dropdown: { fontSize: 13 } }} />
          </div>

          {/* Separator */}
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />

          {/* Organisation group */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#C4C4C4',
              textTransform: 'uppercase', letterSpacing: '0.07em',
            }}>Organisation</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>Directorate</span>
                <Dropdown
                  multiSelect
                  placeholder="All directorates"
                  options={(filterOpts?.directorates ?? []).map(d => ({ key: d, text: d }))}
                  selectedKeys={directorate}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = String(o.key);
                    setDirectorate(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  disabled={filterOptsLoading || !filterOpts?.directorates.length}
                  styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 300 } }} />
              </div>
              <div className={s.filterGroup}>
                <span className={s.filterLabel} style={{ color: directorate.length ? '#9CA3AF' : '#D1D5DB' }}>
                  Department
                </span>
                <Dropdown
                  multiSelect
                  placeholder="All departments"
                  options={availableDepts.map(d => ({ key: d, text: d }))}
                  selectedKeys={department}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = String(o.key);
                    setDepartment(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  disabled={filterOptsLoading || !availableDepts.length}
                  styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 340 } }} />
              </div>
            </div>
          </div>

          {/* Separator */}
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />

          {/* Employment status */}
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Employment Status</span>
            <Dropdown
              multiSelect
              placeholder="All statuses"
              options={(filterOpts?.employmentStatuses ?? []).map(t => ({ key: t, text: t }))}
              selectedKeys={employmentStatus}
              onChange={(_, o) => {
                if (!o) return;
                const v = String(o.key);
                setEmploymentStatus(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
              }}
              disabled={filterOptsLoading || !filterOpts?.employmentStatuses.length}
              styles={{ root: { minWidth: 175 }, dropdown: { fontSize: 13 }, callout: { minWidth: 260 } }} />
          </div>

          {/* Separator */}
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />

          {/* Gender */}
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Gender</span>
            <Dropdown
              multiSelect
              placeholder="All genders"
              options={(filterOpts?.genders ?? []).map(g => ({ key: g, text: g }))}
              selectedKeys={gender}
              onChange={(_, o) => {
                if (!o) return;
                const v = String(o.key);
                setGender(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
              }}
              disabled={filterOptsLoading || !filterOpts?.genders?.length}
              styles={{ root: { minWidth: 160 }, dropdown: { fontSize: 13 }, callout: { minWidth: 220 } }} />
          </div>

          {/* Year + Month — only on date-driven tabs (override the Period window) */}
          {showDateFilter && (
            <>
              <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>Year</span>
                <Dropdown
                  multiSelect
                  placeholder="Rolling period"
                  options={(filterOpts?.years ?? []).map(y => ({ key: String(y), text: String(y) }))}
                  selectedKeys={year.map(String)}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = Number(o.key);
                    setYear(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  disabled={filterOptsLoading || !filterOpts?.years?.length}
                  styles={{ root: { minWidth: 130 }, dropdown: { fontSize: 13 }, callout: { minWidth: 140 } }} />
              </div>
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>Month</span>
                <Dropdown
                  multiSelect
                  placeholder="All months"
                  options={MONTHS.map(m => ({ key: String(m.key), text: m.text }))}
                  selectedKeys={month.map(String)}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = Number(o.key);
                    setMonth(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  styles={{ root: { minWidth: 130 }, dropdown: { fontSize: 13 }, callout: { minWidth: 150 } }} />
              </div>
            </>
          )}

          {/* Tenure Groups — Departures tabs */}
          {(activeTab === 'departures' || activeTab === 'departureTrends') && (
            <>
              <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>Tenure</span>
                <Dropdown
                  multiSelect
                  placeholder="All tenures"
                  options={['<1 Year', '01 - 03 Years', '03 - 05 Years', '05 - 10 Years', '10 - 15 Years', '15+ Years']
                    .map(t => ({ key: t, text: t }))}
                  selectedKeys={tenureGroup}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = String(o.key);
                    setTenureGroup(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  styles={{ root: { minWidth: 150 }, dropdown: { fontSize: 13 }, callout: { minWidth: 170 } }} />
              </div>
            </>
          )}

          {/* Excessive AL Category — Excessive Leave tab */}
          {activeTab === 'excessiveLeave' && (
            <>
              <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>AL Category</span>
                <Dropdown
                  multiSelect
                  placeholder="All categories"
                  options={['<30 Days', '30 - 40 Days', 'Greater than 40 Days'].map(c => ({ key: c, text: c }))}
                  selectedKeys={excessiveCategory}
                  onChange={(_, o) => {
                    if (!o) return;
                    const v = String(o.key);
                    setExcessiveCategory(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                  }}
                  styles={{ root: { minWidth: 175 }, dropdown: { fontSize: 13 }, callout: { minWidth: 200 } }} />
              </div>
            </>
          )}

          {/* Leave filter — grouped Category on Leave Taken, balance Type on Balances/Excessive */}
          {showLeaveFilter && (
            <>
              <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
              {activeTab === 'leaveTaken' ? (
                <div className={s.filterGroup}>
                  <span className={s.filterLabel}>Leave Category</span>
                  <Dropdown
                    multiSelect
                    placeholder="All categories"
                    options={['Leave', 'Personal Leave', 'Parental Leave'].map(g => ({ key: g, text: g }))}
                    selectedKeys={leaveGroup}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setLeaveGroup(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    styles={{ root: { minWidth: 165 }, dropdown: { fontSize: 13 }, callout: { minWidth: 200 } }} />
                </div>
              ) : (
                <div className={s.filterGroup}>
                  <span className={s.filterLabel}>Leave Type</span>
                  <Dropdown
                    multiSelect
                    placeholder="All leave types"
                    options={(filterOpts?.leaveTypes ?? []).map(t => ({ key: t, text: t }))}
                    selectedKeys={leaveType}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setLeaveType(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !filterOpts?.leaveTypes.length}
                    styles={{ root: { minWidth: 155 }, dropdown: { fontSize: 13 }, callout: { minWidth: 200 } }} />
                </div>
              )}

              {/* Hours / Days unit toggle */}
              <div className={s.filterGroup}>
                <span className={s.filterLabel}>Units<InfoTip text="Leave is stored in hours in the warehouse. Days = hours ÷ 7.6, where 7.6 hours is a standard working day (38-hour week). Switch to Hours to see the raw values." /></span>
                <div style={{ display: 'inline-flex', border: '1px solid #E1E1E1', borderRadius: 6, overflow: 'hidden', height: 32 }}>
                  {(['hours', 'days'] as LeaveUnit[]).map(u => (
                    <button
                      key={u}
                      onClick={() => setLeaveUnit(u)}
                      style={{
                        padding: '0 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        border: 'none', textTransform: 'capitalize',
                        background: leaveUnit === u ? CYAN : '#fff',
                        color: leaveUnit === u ? '#fff' : '#747474',
                      }}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {hasActiveFilter && (
            <button className={s.clearBtn} onClick={clearFilters} style={{ alignSelf: 'flex-end' }}>
              Clear filters
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div className={s.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={s.tabBtn}
              onClick={() => setActiveTab(tab.key)}
              style={{
                color: activeTab === tab.key ? CYAN : '#747474',
                borderBottom: activeTab === tab.key ? `2px solid ${CYAN}` : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Data-source note */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          marginTop: 12, marginBottom: 12,
          fontSize: 12, color: '#6B7280',
          background: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: 6, padding: '8px 12px',
        }}>
          <Info size={14} color="#6B7280" />
          Headcount and leave figures reflect currently-active employees. Some demographic
          attributes (ethnicity, religion, country of birth) are not yet populated in the warehouse.
        </div>

        {/* Loading state */}
        {loading && (
          <div className={s.centeredMsg}>
            <Spinner size={SpinnerSize.large} />
            <span>Loading {TABS.find(t => t.key === activeTab)?.label}…</span>
          </div>
        )}

        {/* Empty/error fallbacks */}
        {!loading && activeTab === 'newStarters'    && !newStartersData    && !error && (
          <EmptyPrompt label="New Starters" />
        )}
        {!loading && activeTab === 'departures'     && !departuresData     && !error && (
          <EmptyPrompt label="Departures" />
        )}
        {!loading && activeTab === 'departureTrends' && !departuresData    && !error && (
          <EmptyPrompt label="Departure Trends" />
        )}
        {!loading && activeTab === 'leaveBalances'  && !leaveBalancesData  && !error && (
          <EmptyPrompt label="Leave Balances" />
        )}
        {!loading && activeTab === 'excessiveLeave' && !excessiveLeaveData && !error && (
          <EmptyPrompt label="Excessive Leave" />
        )}
        {!loading && activeTab === 'leaveTaken'     && !leaveTakenData     && !error && (
          <EmptyPrompt label="Leave Taken" />
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 1: DEMOGRAPHICS
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'demographics' && !loading && demographicsData && (() => {
          const { kpis, byDirectorate, genderSeries, byStatusGender, byAgeGender, byTenureGender, byGender } = demographicsData;
          const hc = kpis.headcount;
          const genderTotal = byGender.reduce((sum, g) => sum + g.count, 0);
          const genderPie = byGender.map(g => ({ name: g.gender, value: g.count }));
          const stackBars = (rows: GenderSplitRow[], height: number) => (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={rows} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {genderSeries.map(g => (
                  <Bar key={g} dataKey={g} stackId="g" fill={GENDER_COLOURS[g] ?? CYAN} maxBarSize={52} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          );
          return (
            <>
              {/* KPIs: Headcount · Total FTE (EFT) · Avg Tenure · Avg Age */}
              <div className={s.kpiRow4}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Headcount<InfoTip text="Active employees only. Active = PayGlobal TerminationDate is the 1970-01-01 sentinel (never terminated) or is dated in the future (a planned termination not yet effective)." /></div>
                  <div className={s.kpiValue} style={{ color: CYAN }}>{fmtNum(hc)}</div>
                  <div className={s.kpiSub}>currently active employees</div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>EFT<InfoTip text="Equivalent Full Time — the sum of appointment FTE across active employees (a 0.6 FTE part-timer counts as 0.6, not 1). Sourced from Fact_Appointment_FTE." /></div>
                  <div className={s.kpiValue}>{fmtDec(kpis.totalFTE)}</div>
                  <div className={s.kpiSub}>equivalent full time (sum of FTE)</div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg Tenure<InfoTip text="Average length of service — years from each active employee's StartDate to today, then averaged." /></div>
                  <div className={s.kpiValue}>
                    {fmtDec(kpis.avgTenureYears)}<span style={{ fontSize: 18, fontWeight: 600, marginLeft: 3 }}>yrs</span>
                  </div>
                  <div className={s.kpiSub}>years since start date</div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg Age<InfoTip text="Average age — years from each active employee's BirthDate to today, then averaged." /></div>
                  <div className={s.kpiValue}>
                    {fmtDec(kpis.avgAgeYears)}<span style={{ fontSize: 18, fontWeight: 600, marginLeft: 3 }}>yrs</span>
                  </div>
                  <div className={s.kpiSub}>from date of birth</div>
                </div>
              </div>

              {/* Row 1: Gender donut + Age Range by gender */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Gender</div>
                  <div className={s.sectionSub}>{fmtNum(genderTotal)} active employees</div>
                  {genderPie.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 240 }}>No gender data</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={genderPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                            innerRadius={55} outerRadius={92} paddingAngle={2}>
                            {genderPie.map((g, i) => (
                              <Cell key={i} fill={GENDER_COLOURS[g.name] ?? CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle}
                            formatter={(v: any, n: any) => [fmtNum(v), n]} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Age Range</div>
                  <div className={s.sectionSub}>Age bands, split by gender</div>
                  {stackBars(byAgeGender, 240)}
                </div>
              </div>

              {/* Row 2: Employee Status by gender + Tenure by gender */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Employee Status</div>
                  <div className={s.sectionSub}>Employment type, split by gender</div>
                  {stackBars(byStatusGender, 260)}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Tenure</div>
                  <div className={s.sectionSub}>Years of service, split by gender</div>
                  {stackBars(byTenureGender, 260)}
                </div>
              </div>

              {/* Row 3: Headcount by Directorate */}
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}>Headcount by Directorate</div>
                <div className={s.sectionSub}>{byDirectorate.length} directorates · {fmtNum(hc)} active</div>
                <ResponsiveContainer width="100%" height={Math.max(160, byDirectorate.length * 40)}>
                  <BarChart data={byDirectorate} layout="vertical"
                    margin={{ top: 0, right: 50, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                    <YAxis type="category" dataKey="directorate" width={200}
                      tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                    <Tooltip content={<CountTooltip total={hc} />} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={24}>
                      {byDirectorate.map((e, i) => (
                        <Cell key={i} fill={dirColour(e.directorate, i)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 2: NEW STARTERS
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'newStarters' && !loading && newStartersData && (() => {
          const { kpis, genderSeries, byStatusGender, byAgeGender, byGender, byMonth, byDirectorate, detail } = newStartersData;
          const genderPie = byGender.map(g => ({ name: g.gender, value: g.count }));
          const stackBars = (rows: GenderSplitRow[], height: number) => (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={rows} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {genderSeries.map(g => (
                  <Bar key={g} dataKey={g} stackId="g" fill={GENDER_COLOURS[g] ?? CYAN} maxBarSize={52} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          );
          return (
            <>
              <div className={s.kpiRow4}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Starters</div>
                  <div className={s.kpiValue} style={{ color: CYAN }}>{fmtNum(kpis.totalStarters)}</div>
                  <div className={s.kpiSub}>started in selected period</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg Age</div>
                  <div className={s.kpiValue}>
                    {kpis.avgAgeYears != null
                      ? <>{fmtDec(kpis.avgAgeYears)}<span style={{ fontSize: 18, fontWeight: 600, marginLeft: 3 }}>yrs</span></>
                      : '—'}
                  </div>
                  <div className={s.kpiSub}>from date of birth</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Full-Time</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.fullTime)}</div>
                  <div className={s.kpiSub}>new full-time employees</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Part-Time</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.partTime)}</div>
                  <div className={s.kpiSub}>new part-time employees</div>
                </div>
              </div>

              {/* Row 1: Gender donut + Age Range by gender */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Gender</div>
                  <div className={s.sectionSub}>{fmtNum(kpis.totalStarters)} starters</div>
                  {genderPie.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 240 }}>No gender data</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={genderPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                            innerRadius={55} outerRadius={92} paddingAngle={2}>
                            {genderPie.map((g, i) => (
                              <Cell key={i} fill={GENDER_COLOURS[g.name] ?? CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} formatter={(v: any, n: any) => [fmtNum(v), n]} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Age Range</div>
                  <div className={s.sectionSub}>New starters by age band, split by gender</div>
                  {stackBars(byAgeGender, 240)}
                </div>
              </div>

              {/* Row 2: Employee Status by gender + Starters by Month */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Employee Status</div>
                  <div className={s.sectionSub}>New starters by status, split by gender</div>
                  {stackBars(byStatusGender, 240)}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Starters by Month</div>
                  <div className={s.sectionSub}>Filtered by start date</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No starters for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="starterGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={CYAN} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={CYAN} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="count" name="Starters" stroke={CYAN}
                            strokeWidth={2} fill="url(#starterGrad)" dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {/* Row 3: Starters by Directorate */}
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}>Starters by Directorate</div>
                <div className={s.sectionSub}>Filtered period</div>
                {byDirectorate.length === 0
                  ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={Math.max(160, byDirectorate.length * 40)}>
                      <BarChart data={byDirectorate} layout="vertical"
                        margin={{ top: 0, right: 50, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                        <YAxis type="category" dataKey="directorate" width={200}
                          tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                        <Tooltip formatter={(v: any) => [fmtNum(v), 'Starters']}
                          labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={24}>
                          {byDirectorate.map((e, i) => (
                            <Cell key={i} fill={dirColour(e.directorate, i)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
              </div>

              {detail.length > 0 && (
                <SummaryTable
                  title="New Starters by Department"
                  subtitle={`${fmtNum(detail.length)} departments with starters · use filters to narrow down`}
                  rows={detail}
                  searchPlaceholder="Search department or directorate…"
                  columns={[
                    { header: 'Department', render: r => <DeptCell dep={r.department} dir={r.directorate} /> },
                    { header: 'Starters', render: r => fmtNum(r.starters), nowrap: true, bold: true },
                    { header: 'Full-Time', render: r => fmtNum(r.fullTime), nowrap: true },
                    { header: 'Part-Time', render: r => fmtNum(r.partTime), nowrap: true },
                    { header: 'Casual', render: r => fmtNum(r.casual), nowrap: true },
                  ]}
                  searchKeys={r => [r.department, r.directorate]}
                />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 3: DEPARTURES / TURNOVER
           ══════════════════════════════════════════════════════════════════════ */}
        {/* Departures 1 — who left (demographic breakdown) */}
        {activeTab === 'departures' && !loading && departuresData && (() => {
          const { kpis, genderSeries, byGender, byStatusGender, byAgeGender, byTenureGender } = departuresData;
          const genderPie = byGender.map(g => ({ name: g.gender, value: g.count }));
          const stackBars = (rows: GenderSplitRow[], height: number) => (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={rows} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {genderSeries.map(g => (
                  <Bar key={g} dataKey={g} stackId="g" fill={GENDER_COLOURS[g] ?? CYAN} maxBarSize={52} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          );
          return (
            <>
              <div className={s.kpiRow3}>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${AMBER}` }}>
                  <div className={s.kpiLabel}>Departures</div>
                  <div className={s.kpiValue} style={{ color: kpis.totalLeavers > 0 ? AMBER : GREEN }}>
                    {fmtNum(kpis.totalLeavers)}
                  </div>
                  <div className={s.kpiSub}>terminated in selected period</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Turnover<InfoTip text="Leavers in the selected period ÷ current active headcount, shown as a percentage. A leaver has a real TerminationDate in the period (after the 1970-01-01 sentinel and on or before today)." /></div>
                  <div className={s.kpiValue} style={{ color: AMBER }}>
                    {kpis.turnoverPct != null
                      ? <>{fmtDec(kpis.turnoverPct)}<span style={{ fontSize: 20, fontWeight: 600, marginLeft: 2 }}>%</span></>
                      : '—'}
                  </div>
                  <div className={s.kpiSub}>leavers ÷ active headcount</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Active Headcount</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.activeHeadcount)}</div>
                  <div className={s.kpiSub}>turnover denominator</div>
                </div>
              </div>

              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Gender</div>
                  <div className={s.sectionSub}>{fmtNum(kpis.totalLeavers)} departures</div>
                  {genderPie.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 240 }}>No departures for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={genderPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                            innerRadius={55} outerRadius={92} paddingAngle={2}>
                            {genderPie.map((g, i) => (
                              <Cell key={i} fill={GENDER_COLOURS[g.name] ?? CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} formatter={(v: any, n: any) => [fmtNum(v), n]} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                </div>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Age Range</div>
                  <div className={s.sectionSub}>Leavers by age band, split by gender</div>
                  {stackBars(byAgeGender, 240)}
                </div>
              </div>

              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Employee Status</div>
                  <div className={s.sectionSub}>Leavers by status, split by gender</div>
                  {stackBars(byStatusGender, 240)}
                </div>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Tenure</div>
                  <div className={s.sectionSub}>Years of service at exit, split by gender</div>
                  {stackBars(byTenureGender, 240)}
                </div>
              </div>
            </>
          );
        })()}

        {/* Departures 2 — why & when (reasons / status / trend) */}
        {activeTab === 'departureTrends' && !loading && departuresData && (() => {
          const { kpis, byMonth, byDirectorate, byReason, byStatus, detail } = departuresData;
          const statusPie = byStatus.map(st => ({ name: st.status, value: st.count }));
          return (
            <>
              <div className={s.kpiRow3}>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${AMBER}` }}>
                  <div className={s.kpiLabel}>Departures</div>
                  <div className={s.kpiValue} style={{ color: kpis.totalLeavers > 0 ? AMBER : GREEN }}>
                    {fmtNum(kpis.totalLeavers)}
                  </div>
                  <div className={s.kpiSub}>terminated in selected period</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Turnover<InfoTip text="Leavers in the selected period ÷ current active headcount, shown as a percentage. A leaver has a real TerminationDate in the period (after the 1970-01-01 sentinel and on or before today)." /></div>
                  <div className={s.kpiValue} style={{ color: AMBER }}>
                    {kpis.turnoverPct != null
                      ? <>{fmtDec(kpis.turnoverPct)}<span style={{ fontSize: 20, fontWeight: 600, marginLeft: 2 }}>%</span></>
                      : '—'}
                  </div>
                  <div className={s.kpiSub}>leavers ÷ active headcount</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Reasons</div>
                  <div className={s.kpiValue}>{fmtNum(byReason.length)}</div>
                  <div className={s.kpiSub}>distinct departure reasons</div>
                </div>
              </div>

              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Departures by Reason</div>
                  <div className={s.sectionSub}>{byReason.length} reasons · filtered period</div>
                  {byReason.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 200 }}>No departures for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(200, byReason.length * 32)}>
                        <BarChart data={byReason} layout="vertical"
                          margin={{ top: 0, right: 56, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                          <YAxis type="category" dataKey="reason" width={200}
                            tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                          <Tooltip formatter={(v: any) => [fmtNum(v), 'Employees']}
                            labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20} fill={CYAN}>
                            <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: '#6B7280' }} formatter={fmtNum} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Departures by Employee Status</div>
                  <div className={s.sectionSub}>Share of leavers by status</div>
                  {statusPie.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 240 }}>No data</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                            innerRadius={55} outerRadius={92} paddingAngle={2}>
                            {statusPie.map((_, i) => (
                              <Cell key={i} fill={CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} formatter={(v: any, n: any) => [fmtNum(v), n]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Departures by Month</div>
                  <div className={s.sectionSub}>Filtered by termination date</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No departures for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="leaverGrad2" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={AMBER} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={AMBER} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="count" name="Departures" stroke={AMBER}
                            strokeWidth={2} fill="url(#leaverGrad2)" dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Departures by Directorate</div>
                  <div className={s.sectionSub}>Filtered period</div>
                  {byDirectorate.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No data</div>
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(160, byDirectorate.length * 40)}>
                        <BarChart data={byDirectorate} layout="vertical"
                          margin={{ top: 0, right: 50, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                          <YAxis type="category" dataKey="directorate" width={200}
                            tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                          <Tooltip formatter={(v: any) => [fmtNum(v), 'Departures']}
                            labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={24}>
                            {byDirectorate.map((e, i) => (
                              <Cell key={i} fill={dirColour(e.directorate, i)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {detail.length > 0 && (
                <SummaryTable
                  title="Departures by Reason"
                  subtitle={`${fmtNum(detail.length)} reasons · share of all departures in period`}
                  rows={detail}
                  searchPlaceholder="Search reason…"
                  columns={[
                    { header: 'Reason', render: r => r.reason || '—' },
                    { header: 'Departures', render: r => fmtNum(r.count), nowrap: true, bold: true },
                    { header: '% of Total', render: r => `${fmtDec(r.pct)}%`, nowrap: true },
                  ]}
                  searchKeys={r => [r.reason]}
                />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 4: LEAVE BALANCES
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'leaveBalances' && !loading && leaveBalancesData && (() => {
          const { byLeaveType, byDirectorate, detail } = leaveBalancesData;
          const leaveTypesInData = byLeaveType.map(l => l.leaveType);
          // Convert chart source (hours → selected unit) for display.
          const byTypeDisp = byLeaveType.map((l, i) => ({
            ...l, totalDisp: uv(l.totalUnits) ?? 0, colour: leaveColour(l.leaveType, i),
          }));
          const byDirDisp = byDirectorate.map(d => {
            const out: Record<string, any> = { directorate: d.directorate };
            leaveTypesInData.forEach(lt => { out[lt] = uv((d as any)[lt] ?? 0) ?? 0; });
            return out;
          });
          return (
            <>
              {/* KPI cards — avg balance per leave type */}
              {byLeaveType.length > 0 ? (
                <div className={s.kpiRow4}>
                  {byLeaveType.slice(0, 4).map((lt, i) => (
                    <div key={lt.leaveType} className={s.kpiCard}
                      style={{ borderLeft: `3px solid ${leaveColour(lt.leaveType, i)}` }}>
                      <div className={s.kpiLabel}>{lt.leaveType} · Avg Balance</div>
                      <div className={s.kpiValue} style={{ color: leaveColour(lt.leaveType, i) }}>
                        {fmtDec(uv(lt.avgUnits))}
                      </div>
                      <div className={s.kpiSub}>
                        {fmtNum(lt.employeeCount)} employees · {fmtDec(uv(lt.totalUnits))} {unitLabel} total
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={s.centeredMsg} style={{ minHeight: 160 }}>No leave-balance data</div>
              )}

              {/* Chart: total balance by leave type */}
              {byLeaveType.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Total Balance by Leave Type</div>
                  <div className={s.sectionSub}>Sum of balance {unitLabel} across active employees</div>
                  <ResponsiveContainer width="100%" height={Math.max(160, byTypeDisp.length * 44)}>
                    <BarChart data={byTypeDisp} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="leaveType" width={80}
                        tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: any) => [`${fmtNum(v)} ${unitLabel}`, 'Balance']}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="totalDisp" radius={[0, 4, 4, 0]} barSize={26}>
                        {byTypeDisp.map((e, i) => (
                          <Cell key={i} fill={e.colour} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Chart: stacked balance by directorate × leave type */}
              {byDirectorate.length > 0 && leaveTypesInData.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Leave Balance by Directorate</div>
                  <div className={s.sectionSub}>Stacked by leave type · total balance {unitLabel}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 14 }}>
                    {leaveTypesInData.map((lt, i) => (
                      <div key={lt} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 11, height: 11, borderRadius: 2, background: leaveColour(lt, i) }} />
                        <span style={{ fontSize: 12, color: '#484848' }}>{lt}</span>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(180, byDirDisp.length * 40)}>
                    <BarChart data={byDirDisp} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="directorate" width={200}
                        tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                      <Tooltip formatter={(v: any, n: any) => [`${fmtNum(v)} ${unitLabel}`, n]}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      {leaveTypesInData.map((lt, i) => (
                        <Bar key={lt} dataKey={lt} name={lt} stackId="a"
                          fill={leaveColour(lt, i)} barSize={22} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {detail.length > 0 && (
                <SummaryTable
                  title="Average Leave Balance by Department"
                  subtitle={`${fmtNum(detail.length)} departments · average balance per employee, in ${unitLabel}`}
                  rows={detail}
                  searchPlaceholder="Search department or directorate…"
                  columns={[
                    { header: 'Department', render: r => <DeptCell dep={r.department} dir={r.directorate} /> },
                    { header: 'Employees', render: r => fmtNum(r.employees), nowrap: true },
                    { header: `Avg AL (${unitLabel})`,  render: r => fmtDec(uv(r.AL)),  nowrap: true, bold: true },
                    { header: `Avg SL (${unitLabel})`,  render: r => fmtDec(uv(r.SL)),  nowrap: true },
                    { header: `Avg LSL (${unitLabel})`, render: r => fmtDec(uv(r.LSL)), nowrap: true },
                    { header: `Avg RDO (${unitLabel})`, render: r => fmtDec(uv(r.RDO)), nowrap: true },
                  ]}
                  searchKeys={r => [r.department, r.directorate]}
                />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 5: EXCESSIVE LEAVE
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'excessiveLeave' && !loading && excessiveLeaveData && (() => {
          const { kpis, bands, bandTotals, byDepartment, detail } = excessiveLeaveData;
          const EXCESS_BANDS = ['30 - 40 Days', 'Greater than 40 Days'];
          const BAND_COLOURS: Record<string, string> = {
            '<30 Days': '#67E8F9', '30 - 40 Days': '#0891B2', 'Greater than 40 Days': '#D97706',
          };
          const num = (v: number | string | undefined) => Number(v) || 0;
          const chartDepts = byDepartment.filter(
            d => num(d['30 - 40 Days']) + num(d['Greater than 40 Days']) > 0
          );
          return (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14,
                fontSize: 12, color: '#6B7280',
                background: '#F9FAFB', border: '1px solid #E5E7EB',
                borderRadius: 6, padding: '8px 12px',
              }}>
                <Info size={14} color="#6B7280" />
                Active employees banded by Annual Leave balance: under 30 days, 30–40 days, and over 40 days.
                <InfoTip text="Each active employee's Annual Leave balance (hours ÷ 7.6 = days) is placed in a band: under 30, 30–40, or over 40 days. Over 40 days is the excessive-leave threshold." />
              </div>

              <div className={s.kpiRow3}>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${AMBER}` }}>
                  <div className={s.kpiLabel}>Greater than 40 Days</div>
                  <div className={s.kpiValue} style={{ color: kpis.overThreshold > 0 ? AMBER : GREEN }}>
                    {fmtNum(kpis.overThreshold)}
                  </div>
                  <div className={s.kpiSub}>employees over 40 AL days</div>
                </div>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${BAND_COLOURS['30 - 40 Days']}` }}>
                  <div className={s.kpiLabel}>30 – 40 Days</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.band3040)}</div>
                  <div className={s.kpiSub}>employees in the 30–40 AL day band</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Under 30 Days</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.under30)}</div>
                  <div className={s.kpiSub}>employees under 30 AL days</div>
                </div>
              </div>

              {chartDepts.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Annual Balance by Department</div>
                  <div className={s.sectionSub}>Employees in the 30–40 and over-40-day AL bands</div>
                  <ResponsiveContainer width="100%" height={Math.max(200, chartDepts.length * 30)}>
                    <BarChart data={chartDepts} layout="vertical"
                      margin={{ top: 0, right: 40, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                      <YAxis type="category" dataKey="department" width={220}
                        tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 32)} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {EXCESS_BANDS.map(b => (
                        <Bar key={b} dataKey={b} stackId="e" fill={BAND_COLOURS[b]} maxBarSize={22} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className={s.sectionCard}>
                <div className={s.sectionTitle}>Annual Leave Category by Department</div>
                <div className={s.sectionSub}>
                  Employee counts by AL band · {fmtNum(bands.reduce((t, b) => t + (bandTotals[b] || 0), 0))} active
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>
                        <th style={{ padding: '8px 10px' }}>Department</th>
                        {bands.map(b => (
                          <th key={b} style={{ padding: '8px 10px', textAlign: 'right' }}>{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {byDepartment.map((d, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '7px 10px' }}>{d.department}</td>
                          {bands.map(b => (
                            <td key={b} style={{ padding: '7px 10px', textAlign: 'right',
                              fontWeight: b !== '<30 Days' && num(d[b]) > 0 ? 600 : 400,
                              color: b === 'Greater than 40 Days' && num(d[b]) > 0 ? AMBER : undefined }}>
                              {num(d[b]) ? fmtNum(num(d[b])) : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #E5E7EB', fontWeight: 700 }}>
                        <td style={{ padding: '8px 10px' }}>Total</td>
                        {bands.map(b => (
                          <td key={b} style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtNum(bandTotals[b] || 0)}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {detail.length > 0 && (
                <SummaryTable
                  title="Annual Leave Bands by Directorate"
                  subtitle={`${fmtNum(detail.length)} directorates · AL balances in ${unitLabel}`}
                  rows={detail}
                  searchPlaceholder="Search directorate…"
                  columns={[
                    { header: 'Directorate', render: r => r.directorate || '—' },
                    { header: 'Over 40 Days', render: r => fmtNum(r.over40), nowrap: true, bold: true },
                    { header: '30–40 Days', render: r => fmtNum(r.band3040), nowrap: true },
                    { header: 'Employees', render: r => fmtNum(r.employees), nowrap: true },
                    { header: `Avg AL (${unitLabel})`, render: r => fmtDec(uv(r.avgAL)), nowrap: true },
                    { header: `Highest AL (${unitLabel})`, render: r => fmtDec(uv(r.maxAL)), nowrap: true },
                  ]}
                  searchKeys={r => [r.directorate]}
                />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 6: LEAVE TAKEN
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'leaveTaken' && !loading && leaveTakenData && (() => {
          const { kpis, leaveGroups, byGroup, byLeaveType, byMonthGroup, detail } = leaveTakenData;
          const unitTitle = leaveUnit === 'days' ? 'Days' : 'Hours';
          const totalDisp = uv(kpis.totalHours) ?? 0;
          const avgDisp   = kpis.employees > 0 ? uv(kpis.totalHours / kpis.employees) : null;
          const GROUP_COLOURS: Record<string, string> = {
            'Leave': '#06B6D4', 'Personal Leave': '#D97706', 'Parental Leave': '#7C3AED',
          };
          const byTypeDisp  = byLeaveType.slice(0, 15).map(a => ({ ...a, disp: uv(a.hours) ?? 0 }));
          const byGroupDisp = byGroup.map(g => ({ ...g, disp: uv(g.hours) ?? 0 }));
          const monthDisp = byMonthGroup.map(m => {
            const row: Record<string, number | string> = { label: m.label };
            leaveGroups.forEach(g => { row[g] = uv(Number(m[g])) ?? 0; });
            return row;
          });
          return (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14,
                fontSize: 12, color: '#6B7280',
                background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 12px',
              }}>
                <Info size={14} color="#6B7280" />
                Grouped into Leave / Personal / Parental. Annual, Sick &amp; Long-Service leave taken come from the
                working-hours fact (sparse in the dev test copy; complete in production).
              </div>

              <div className={s.kpiRow3}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Leave {unitTitle}<InfoTip text="Leave taken combines the leave-transaction fact with Annual, Sick and Long-Service hours from the working-hours fact, grouped into Leave / Personal / Parental. Hours ÷ 7.6 = days." /></div>
                  <div className={s.kpiValue} style={{ color: CYAN }}>{fmtNum(Math.round(totalDisp))}</div>
                  <div className={s.kpiSub}>{unitLabel} taken in selected period</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Employees</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.employees)}</div>
                  <div className={s.kpiSub}>with leave taken in period</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg {unitTitle} / Employee</div>
                  <div className={s.kpiValue}>{avgDisp != null ? fmtDec(avgDisp) : '—'}</div>
                  <div className={s.kpiSub}>mean {unitLabel} per employee</div>
                </div>
              </div>

              <div className={s.sectionCard}>
                <div className={s.sectionTitle}>Leave {unitTitle} by Month</div>
                <div className={s.sectionSub}>Stacked by leave category · filtered by leave date</div>
                {monthDisp.length === 0
                  ? <div className={s.centeredMsg} style={{ minHeight: 200 }}>No leave taken in this period</div>
                  : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={monthDisp} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {leaveGroups.map(g => (
                          <Bar key={g} dataKey={g} stackId="lt" fill={GROUP_COLOURS[g] ?? CYAN} maxBarSize={34} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
              </div>

              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Leave {unitTitle} by Category</div>
                  <div className={s.sectionSub}>Leave · Personal · Parental</div>
                  <ResponsiveContainer width="100%" height={Math.max(150, byGroupDisp.length * 46)}>
                    <BarChart data={byGroupDisp} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="group" width={110}
                        tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 16)} />
                      <Tooltip formatter={(v: any) => [`${fmtNum(v)} ${unitLabel}`, 'Leave']}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="disp" radius={[0, 4, 4, 0]} barSize={26}>
                        {byGroupDisp.map((g, i) => (
                          <Cell key={i} fill={GROUP_COLOURS[g.group] ?? CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Leave {unitTitle} by Type</div>
                  <div className={s.sectionSub}>Top leave types · filtered period</div>
                  {byTypeDisp.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No leave taken in this period</div>
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(180, byTypeDisp.length * 30)}>
                        <BarChart data={byTypeDisp} layout="vertical"
                          margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                          <YAxis type="category" dataKey="leaveType" width={180}
                            tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 24)} />
                          <Tooltip formatter={(v: any) => [`${fmtNum(v)} ${unitLabel}`, 'Leave']}
                            labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                          <Bar dataKey="disp" radius={[0, 4, 4, 0]} barSize={16}>
                            {byTypeDisp.map((a, i) => (
                              <Cell key={i} fill={GROUP_COLOURS[a.group] ?? CYAN_PALETTE[i % CYAN_PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {detail.length > 0 && (
                <SummaryTable
                  title="Leave Taken by Type"
                  subtitle={`${fmtNum(detail.length)} leave types · totals in ${unitLabel} · use filters to narrow down`}
                  rows={detail}
                  searchPlaceholder="Search leave type or category…"
                  columns={[
                    { header: 'Leave Type', render: r => r.allowanceType },
                    { header: 'Category', render: r => r.group },
                    { header: `Total ${unitTitle}`, render: r => fmtDec(uv(r.hours)), nowrap: true, bold: true },
                    { header: 'Employees', render: r => fmtNum(r.employees), nowrap: true },
                    { header: `Avg ${unitTitle} / Emp`, render: r => fmtDec(uv(r.avgHours)), nowrap: true },
                  ]}
                  searchKeys={r => [r.allowanceType, r.group]}
                />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 7: HEALTH & SAFETY (coming soon)
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'healthSafety' && (
          <ComingSoon
            icon={<Heartbeat size={42} color="#9CA3AF" weight="duotone" />}
            text="Health & Safety metrics (incidents, RTW) are coming soon — data source not yet integrated."
          />
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 8: EAP / LTIFR (coming soon)
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'eapLtifr' && (
          <ComingSoon
            icon={<ShieldCheck size={42} color="#9CA3AF" weight="duotone" />}
            text="EAP utilisation and LTIFR metrics are coming soon — data source not yet integrated."
          />
        )}

        {/* Loading overlay (refreshing while data visible) */}
        {loading && (demographicsData || newStartersData || departuresData
          || leaveBalancesData || excessiveLeaveData || leaveTakenData) && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
          }}>
            <Spinner size={SpinnerSize.large} label="Refreshing…" />
          </div>
        )}

      </div>
    </div>
  );
}

// ── Empty prompt ──────────────────────────────────────────────────────────────
function EmptyPrompt({ label }: { label: string }) {
  return (
    <div className={s.centeredMsg} style={{ minHeight: 200 }}>
      <ArrowsClockwise size={24} color="#9CA3AF" />
      <span>No data loaded — click <strong>Refresh</strong> to load {label}.</span>
    </div>
  );
}

// ── Coming-soon placeholder ───────────────────────────────────────────────────
function ComingSoon({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className={s.sectionCard} style={{ marginTop: 4 }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 280, gap: 16, textAlign: 'center',
        padding: '32px 16px',
      }}>
        {icon}
        <div style={{ fontSize: 15, fontWeight: 700, color: '#484848' }}>Coming soon</div>
        <div style={{ fontSize: 13, color: '#9CA3AF', maxWidth: 420 }}>{text}</div>
      </div>
    </div>
  );
}

// ── Shared employee detail table ──────────────────────────────────────────────

const TABLE_PAGE_SIZE = 50;

const tdBase: React.CSSProperties = {
  padding: '7px 10px', borderBottom: '1px solid #F3F4F6',
  fontSize: 12, verticalAlign: 'top', color: '#232323',
};
const thBase: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 700,
  fontSize: 11, color: '#747474', background: '#F9FAFB',
  borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap',
};

function DeptCell({ dep, dir }: { dep: string; dir: string }) {
  return (
    <>
      {dep}
      {dir && dir !== 'Unassigned' && (
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{dir}</div>
      )}
    </>
  );
}

interface ColumnDef<T> {
  header: string;
  render: (row: T) => React.ReactNode;
  mono?: boolean;
  nowrap?: boolean;
  bold?: boolean;
}

function SummaryTable<T>({
  title, subtitle, rows, columns, searchKeys, searchPlaceholder = 'Search…',
}: {
  title: string;
  subtitle: string;
  rows: T[];
  columns: ColumnDef<T>[];
  searchKeys: (row: T) => (string | undefined)[];
  searchPlaceholder?: string;
}) {
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(0);

  React.useEffect(() => { setPage(0); }, [search]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return rows;
    return rows.filter(row =>
      searchKeys(row).some(k => (k || '').toLowerCase().includes(q))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search]);

  const totalPages = Math.ceil(filtered.length / TABLE_PAGE_SIZE);
  const pageRows   = filtered.slice(page * TABLE_PAGE_SIZE, (page + 1) * TABLE_PAGE_SIZE);

  return (
    <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>{subtitle}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '6px 10px', border: '1px solid #E1E1E1', borderRadius: 6,
            fontSize: 13, minWidth: 240, outline: 'none',
          }}
        />
        {search && (
          <button onClick={() => setSearch('')}
            style={{ padding: '6px 10px', background: 'none', border: '1px solid #E1E1E1', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#747474' }}>
            Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF' }}>
          {fmtNum(filtered.length)}{filtered.length < rows.length ? ` of ${fmtNum(rows.length)}` : ''} rows
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map(c => <th key={c.header} style={thBase}>{c.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ ...tdBase, textAlign: 'center', color: '#9CA3AF', padding: 24 }}>No matching records</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                {columns.map(c => (
                  <td key={c.header} style={{
                    ...tdBase,
                    ...(c.mono ? { fontFamily: 'monospace', fontSize: 11 } : {}),
                    ...(c.nowrap ? { whiteSpace: 'nowrap' } : {}),
                    ...(c.bold ? { fontWeight: 700 } : {}),
                  }}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function Paginator({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14 }}>
      <button onClick={() => onChange(0)} disabled={page === 0}
        style={{ padding: '4px 8px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        «
      </button>
      <button onClick={() => onChange(page - 1)} disabled={page === 0}
        style={{ padding: '4px 10px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        ← Prev
      </button>
      <span style={{ fontSize: 12, color: '#747474', minWidth: 120, textAlign: 'center' }}>
        Page {page + 1} of {totalPages}
      </span>
      <button onClick={() => onChange(page + 1)} disabled={page >= totalPages - 1}
        style={{ padding: '4px 10px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page >= totalPages - 1 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        Next →
      </button>
      <button onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1}
        style={{ padding: '4px 8px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page >= totalPages - 1 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        »
      </button>
    </div>
  );
}
